import React, { useState } from "react";

// ── Default payloads sent to API (fields not shown in UI) ────────────────────
const VEHICLE_API_DEFAULTS = {
  transporter_id: 0,
  model: "N/A",
  manufacturer: "N/A",
  year: 2000,
  capacity_kg: 0,
  is_available: true,
  fuel_type: "diesel",
  fitness_expiry: new Date().toISOString().slice(0, 10),
  insurance_expiry: new Date().toISOString().slice(0, 10),
  color: "white",
};

// DRIVER_API_DEFAULTS should match the backend driver POST payload fields from authRoutes.ts (933-961)
// That backend expects: user_id, name, phone, transporter_id, license_number, license_expiry, current_vehicle_id, status, aadhar, address, city, state, upi_id, bank_account_name, bank_account_number, bank_ifsc, bank_name

const DRIVER_API_DEFAULTS = {
  user_id: 1,           // backend rejects 0 (falsy) — change to real user id if available
  transporter_id: 1,    // backend rejects 0 (falsy) — change to real transporter id if needed
  current_vehicle_id: null,
  status: "available",
  aadhar: null,
  address: null,
  city: null,
  state: null,
  upi_id: null,
  bank_account_name: null,
  bank_account_number: null,
  bank_ifsc: null,
  bank_name: null,
};


// ── Empty form states (UI fields only) ───────────────────────────────────────
const emptyVehicleForm = {
  registration_number: "",
  vehicle_number: "",
  vehicle_type: "",
};

const emptyDriverForm = {
  name: "",
  phone: "",
  license_number: "",
  license_expiry: "",
};

const API_URL = process.env.REACT_APP_API_URL ?? "";

// ── Shared style helpers ─────────────────────────────────────────────────────
const labelClass = "block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5";
const inputClass =
  "border border-slate-200 rounded-lg px-4 py-2.5 text-sm w-full bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition placeholder:text-slate-300";
const cardClass = "bg-white border border-slate-100 rounded-2xl shadow-sm p-6 mb-6";

// ── Vehicle Section ───────────────────────────────────────────────────────────
const VehicleSection: React.FC = () => {
  const [form, setForm] = useState({
    ...emptyVehicleForm,
    capacity_kg: "900",
  });
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // NEW: Capacity input validation, flexible for future transporter_id input
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Assume transporter_id required in form or provided elsewhere. For now, just put a placeholder id:
    const transporter_id = 1;
    if (
      !transporter_id ||
      !form.registration_number ||
      !form.vehicle_type ||
      !form.capacity_kg
    ) {
      alert("transporter_id, registration_number, vehicle_type, and capacity_kg are required.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...VEHICLE_API_DEFAULTS,
        ...form,
        transporter_id,
        capacity_kg: Number(form.capacity_kg) || 90,
      };
      const resp = await fetch(`${API_URL}/auth/vehicles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // Surface backend error if present in response body
      if (!resp.ok) {
        let msg = "Failed to add vehicle.";
        try {
          const errBody = await resp.json();
          if (errBody?.error) msg = errBody.error;
        } catch {}
        throw new Error(msg);
      }
      const data = await resp.json();
      if (!data?.id) throw new Error("No vehicle id from backend.");
      setVehicles((prev) => [...prev, { ...form, capacity_kg: payload.capacity_kg, id: data.id }]);
      setForm({ ...emptyVehicleForm, capacity_kg: "" });
    } catch (err: any) {
      alert(err?.message || "Could not save vehicle.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cardClass}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white text-lg">
          🚛
        </div>
        <div>
          <h3 className="font-bold text-lg text-slate-800 leading-tight">Add Vehicle</h3>
          <p className="text-xs text-slate-400">Register a vehicle to the system</p>
        </div>
      </div>

      {/* Saved vehicles list */}
      {vehicles.length > 0 && (
        <div className="mb-5 rounded-xl bg-indigo-50 border border-indigo-100 overflow-hidden">
          <div className="px-4 py-2 bg-indigo-100 text-xs font-semibold uppercase tracking-widest text-indigo-600">
            Added Vehicles ({vehicles.length})
          </div>
          <ul className="divide-y divide-indigo-100">
            {vehicles.map((v, i) => (
              <li key={i} className="px-4 py-2.5 flex items-center justify-between">
                <div>
                  <span className="font-semibold text-indigo-700 text-sm">{v.registration_number}</span>
                  {v.vehicle_number && (
                    <span className="ml-2 text-xs text-slate-400">#{v.vehicle_number}</span>
                  )}
                </div>
                <span className="text-xs bg-white border border-indigo-200 text-indigo-600 px-2 py-0.5 rounded-full">
                  {v.vehicle_type}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className={labelClass}>Registration No. *</label>
            <input
              className={inputClass}
              placeholder="e.g. RJ14CA1234"
              value={form.registration_number}
              onChange={(e) => setForm((p) => ({ ...p, registration_number: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className={labelClass}>Vehicle Number</label>
            <input
              className={inputClass}
              placeholder="e.g. VH-001"
              value={form.vehicle_number}
              onChange={(e) => setForm((p) => ({ ...p, vehicle_number: e.target.value }))}
            />
          </div>
          <div>
            <label className={labelClass}>Vehicle Type *</label>
            <input
              className={inputClass}
              placeholder="e.g. Truck, Pickup, Van"
              value={form.vehicle_type}
              onChange={(e) => setForm((p) => ({ ...p, vehicle_type: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className={labelClass}>Capacity (kg) *</label>
            <input
              className={inputClass}
              type="number"
              min="1"
              placeholder="e.g. 5000"
              value={form.capacity_kg}
              onChange={(e) => setForm((p) => ({ ...p, capacity_kg: e.target.value }))}
              required
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="mt-5 w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition disabled:opacity-60 text-sm tracking-wide"
        >
          {loading ? "Saving…" : "+ Add Vehicle"}
        </button>
      </form>
    </div>
  );
};

// ── Driver Section ────────────────────────────────────────────────────────────
// ── Driver Section ────────────────────────────────────────────────────────────
const DriverSection: React.FC = () => {
  const [form, setForm] = useState(emptyDriverForm);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.license_number || !form.license_expiry) {
      alert("Name, License Number, and License Expiry are required.");
      return;
    }
    setLoading(true);
    try {
      if (editingIndex !== null) {
        setDrivers((prev) =>
          prev.map((d, i) => (i === editingIndex ? { ...d, ...form } : d))
        );
        setEditingIndex(null);
      } else {
        // Merge defaults first, then form values override them
        const payload = { ...DRIVER_API_DEFAULTS, ...form };
        const resp = await fetch(`${API_URL}/auth/drivers`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!resp.ok) {
          let msg = "Failed to create driver.";
          try { const e = await resp.json(); if (e?.error) msg = e.error; } catch {}
          throw new Error(msg);
        }
        const data = await resp.json();
        if (!data?.id) throw new Error("No driver id from backend.");
        setDrivers((prev) => [...prev, { ...form, id: data.id }]);
      }
      setForm(emptyDriverForm);
    } catch (err: any) {
      alert(err?.message || "Could not save driver.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (i: number) => {
    setForm({ ...emptyDriverForm, ...drivers[i] });
    setEditingIndex(i);
  };

  const handleDelete = (i: number) => {
    if (!window.confirm("Remove this driver?")) return;
    setDrivers((prev) => prev.filter((_, idx) => idx !== i));
    if (editingIndex === i) {
      setEditingIndex(null);
      setForm(emptyDriverForm);
    }
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setForm(emptyDriverForm);
  };

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center text-white text-lg">
          👤
        </div>
        <div>
          <h3 className="font-bold text-lg text-slate-800 leading-tight">Add Driver</h3>
          <p className="text-xs text-slate-400">Register a driver to the system</p>
        </div>
      </div>

      {drivers.length > 0 && (
        <div className="mb-5 rounded-xl bg-emerald-50 border border-emerald-100 overflow-hidden">
          <div className="px-4 py-2 bg-emerald-100 text-xs font-semibold uppercase tracking-widest text-emerald-700">
            Added Drivers ({drivers.length})
          </div>
          <ul className="divide-y divide-emerald-100">
            {drivers.map((d, i) => (
              <li key={i} className="px-4 py-2.5 flex items-center justify-between">
                <div>
                  <span className="font-semibold text-emerald-700 text-sm">{d.name}</span>
                  {d.phone && (
                    <span className="ml-2 text-xs text-slate-400">{d.phone}</span>
                  )}
                  <div className="text-xs text-slate-400 mt-0.5">
                    License: {d.license_number} · Expiry: {d.license_expiry}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(i)}
                    className="text-xs px-3 py-1 bg-white border border-slate-200 hover:border-indigo-300 hover:text-indigo-600 rounded-lg transition"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(i)}
                    className="text-xs px-3 py-1 bg-white border border-rose-200 text-rose-500 hover:bg-rose-50 rounded-lg transition"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Full Name *</label>
            <input
              className={inputClass}
              placeholder="Driver's full name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className={labelClass}>Phone</label>
            <input
              className={inputClass}
              placeholder="Mobile number"
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
            />
          </div>
          <div>
            <label className={labelClass}>License Number *</label>
            <input
              className={inputClass}
              placeholder="e.g. RJ14-20110012345"
              value={form.license_number}
              onChange={(e) => setForm((p) => ({ ...p, license_number: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className={labelClass}>License Expiry *</label>
            <input
              type="date"
              className={inputClass}
              value={form.license_expiry}
              onChange={(e) => setForm((p) => ({ ...p, license_expiry: e.target.value }))}
              required
            />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition disabled:opacity-60 text-sm tracking-wide"
          >
            {loading ? "Saving…" : editingIndex !== null ? "Update Driver" : "+ Add Driver"}
          </button>
          {editingIndex !== null && (
            <button
              type="button"
              onClick={handleCancel}
              className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition text-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};


// ── Main Export ───────────────────────────────────────────────────────────────
const AddVehicleDriver: React.FC = () => {
  return (
    <div className="w-full max-w-3xl mx-auto py-8 px-4 md:px-8 bg-slate-50 min-h-screen">
      <div className="mb-8 text-center">
        <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Fleet Management</h2>
        <p className="text-sm text-slate-400 mt-1">Add vehicles and drivers independently</p>
      </div>
      <VehicleSection />
      <DriverSection />
    </div>
  );
};

export default AddVehicleDriver;